/**
 * Personel izin talep formu modal'ı.
 * v3: "İlk izin günü" + "İşbaşı tarihi" mantığı.
 *   - 10 Haziran (ilk izin) → 15 Haziran (işbaşı) = 5 gün izin
 *   - Tek gün izin için kullanıcı ardışık 2 tarih seçer (10-11)
 */

import { useEffect, useState } from 'react'
import { Calendar, Send, Info } from 'lucide-react'
import { Modal } from '@/components/ui/Modal'
import { DatePicker } from '@/components/ui/DatePicker'
import { izinTalebiOlustur, gunFarki } from '@/lib/services/izin-service'
import type { KullaniciPersonel, IsoTarih } from '@/types'

interface Props {
  acik: boolean
  personel: KullaniciPersonel
  onKapat: () => void
  onTalepGonderildi: () => void
}

function tarihIso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function bugunIso(): string {
  return tarihIso(new Date())
}

function gunEkle(iso: string, gunSayisi: number): string {
  const d = new Date(iso + 'T00:00:00')
  d.setDate(d.getDate() + gunSayisi)
  return tarihIso(d)
}

function tarihGoster(iso: string): string {
  if (!iso) return ''
  const d = new Date(iso + 'T00:00:00')
  return d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })
}

export function IzinTalepModal({ acik, personel, onKapat, onTalepGonderildi }: Props) {
  const yarin = (() => {
    const d = new Date()
    d.setDate(d.getDate() + 1)
    return tarihIso(d)
  })()
  const yarinSonrasi = gunEkle(yarin, 1)

  const [baslangic, setBaslangic] = useState(yarin)
  const [bitis, setBitis] = useState(yarinSonrasi)  // Default 1 gün izin
  const [aciklama, setAciklama] = useState('')
  const [gonderiliyor, setGonderiliyor] = useState(false)
  const [hata, setHata] = useState<string | null>(null)

  useEffect(() => {
    if (!acik) return
    setBaslangic(yarin)
    setBitis(yarinSonrasi)
    setAciklama('')
    setHata(null)
  }, [acik]) // eslint-disable-line

  const gunSayisi = baslangic && bitis && bitis > baslangic
    ? gunFarki(baslangic as IsoTarih, bitis as IsoTarih)
    : 0

  // Başlangıç değişince işbaşı'nı da düzelt (en az başlangıç + 1)
  useEffect(() => {
    if (bitis <= baslangic) {
      setBitis(gunEkle(baslangic, 1))
    }
  }, [baslangic, bitis])

  // Son izin günü = işbaşı - 1
  const sonIzinGunu = bitis > baslangic ? gunEkle(bitis, -1) : baslangic

  async function gonder() {
    if (gunSayisi < 1) {
      setHata('İşbaşı tarihi ilk izin gününden sonra olmalı')
      return
    }
    if (baslangic < bugunIso()) {
      setHata('Geçmiş tarih için talep yapılamaz')
      return
    }
    setGonderiliyor(true)
    setHata(null)
    try {
      await izinTalebiOlustur({
        personelId: personel.id,
        baslangicTarihi: baslangic as IsoTarih,
        bitisTarihi: bitis as IsoTarih,
        aciklama: aciklama.trim() || undefined,
      })
      onTalepGonderildi()
    } catch (err) {
      setHata((err as Error).message)
    } finally {
      setGonderiliyor(false)
    }
  }

  return (
    <Modal acik={acik} onKapat={onKapat} baslik="Yeni İzin Talebi" boyut="md">
      <div className="space-y-4">
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 flex items-start gap-2">
          <Calendar className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-purple-900">
            Talep BOSS'a iletilir. Onaylandıktan sonra izin başlar.
            <br />
            <span className="text-xs text-purple-700">Sadece tam gün izin alınabilir.</span>
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              İlk İzin Günü <span className="text-red-500">*</span>
            </label>
            <DatePicker
              value={baslangic}
              onChange={setBaslangic}
              min={bugunIso()}
              placeholder="İlk gün"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              İşbaşı Tarihi <span className="text-red-500">*</span>
            </label>
            <DatePicker
              value={bitis}
              onChange={setBitis}
              min={gunEkle(baslangic, 1)}
              placeholder="İşbaşı"
            />
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 flex items-start gap-2">
          <Info className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-900">
            <strong>İşbaşı tarihi</strong> = işe geri döneceğin gün (izin günü değil).
            <br />Örnek: 10 Haziran → 15 Haziran = 5 gün izin (10, 11, 12, 13, 14 izinli; 15'inde işbaşı).
          </p>
        </div>

        {gunSayisi > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
            <div className="text-center">
              <p className="text-xs text-blue-700 uppercase tracking-wide font-semibold">
                Toplam
              </p>
              <p className="text-3xl font-bold text-blue-900 leading-tight">
                {gunSayisi}
                <span className="text-sm font-normal text-blue-700 ml-1">gün izin</span>
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
              <div className="bg-white rounded-lg p-2 text-center">
                <div className="text-gray-500">Son izin günü</div>
                <div className="font-semibold text-gray-800">{tarihGoster(sonIzinGunu)}</div>
              </div>
              <div className="bg-white rounded-lg p-2 text-center">
                <div className="text-gray-500">İşbaşı</div>
                <div className="font-semibold text-green-700">{tarihGoster(bitis)}</div>
              </div>
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Açıklama (opsiyonel)
          </label>
          <textarea
            value={aciklama}
            onChange={(e) => setAciklama(e.target.value)}
            rows={3}
            maxLength={300}
            placeholder="Örn: Aile ziyareti, doktor randevusu..."
            className="input-base resize-none"
          />
        </div>

        {hata && (
          <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-xl p-3">
            {hata}
          </div>
        )}

        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={onKapat}
            disabled={gonderiliyor}
            className="btn-secondary flex-1"
          >
            İptal
          </button>
          <button
            type="button"
            onClick={gonder}
            disabled={gonderiliyor || gunSayisi < 1}
            className="btn-primary flex-1 inline-flex items-center justify-center gap-2"
          >
            <Send className="w-4 h-4" />
            {gonderiliyor ? 'Gönderiliyor...' : 'Talebi Gönder'}
          </button>
        </div>
      </div>
    </Modal>
  )
}
